/**
 * User-level timing actions (W2-08 timing): what a button, a key or the palette does, shared by the commands of
 * timing.module.tsx and the panels. Each reports its outcome with a toast where the user needs to know.
 *
 *  - detectTempo / detectOffset: through the analysis session (worker, persist cache, envelope fallback);
 *  - beatZeroAt: "click a downbeat": beat 0 moves to a music time (decoded time, the preview audio delay removed);
 *  - answerByEar: "Too early / Just right / Too late" with shrinking steps; Just right confirms the timing;
 *  - playWithClicks: playback with the metronome forced on, restored when playback stops;
 *  - add tempo / meter changes at the playhead (snapped beat).
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { TempoCandidate } from '../../core/audio-dsp/types';
import type { MessageRef } from '../../core/util/types';
import { m } from './timing.messages';
import { timingAnalysisFor, type OffsetResult } from './timingAnalysis';
import { playheadBeat, viewOf } from './timingDoc';
import { addMeterAt, addTempoAt, nudgeOffset, setConfirmed, setHeardOffset } from './timingEdits';
import { byEarDelta, protocolBpmList, type ByEarAnswer } from './timingModel';
import { timingSessionFor } from './timingSession';

type Services = Pick<
  AppServices,
  'store' | 'ui' | 'editor' | 'persist' | 'dsp' | 'audio' | 'settings' | 'registry' | 'i18n'
>;

const ref = (key: string, params?: MessageRef['params']): MessageRef =>
  params === undefined ? { key } : { key, params };

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Detects the tempo candidates of the open project's song (force re-runs). */
export async function detectTempo(s: Services, force = false): Promise<readonly TempoCandidate[] | null> {
  const store = s.store;
  const audio = store?.doc.meta.music ?? null;
  if (store === null || audio === null) {
    s.ui.toast(ref(store === null ? m.noProjectReason : m.noSongReason), { kind: 'warning' });
    return null;
  }
  try {
    return await timingAnalysisFor(s).detectTempo(audio, { force });
  } catch (err) {
    s.ui.toast(ref(m.analysisFailed, { error: errText(err) }), { kind: 'error' });
    return null;
  }
}

/** Confidence below which a detected offset is reported as uncertain. */
export const LOW_CONFIDENCE = 0.5;

/** Detects the first beat and moves beat 0 there (one undoable edit). */
export async function detectOffset(s: Services): Promise<OffsetResult | null> {
  const store = s.store;
  const audio = store?.doc.meta.music ?? null;
  if (store === null || audio === null) {
    s.ui.toast(ref(store === null ? m.noProjectReason : m.noSongReason), { kind: 'warning' });
    return null;
  }
  const view = viewOf(store);
  let r: OffsetResult | null;
  try {
    r = await timingAnalysisFor(s).suggestOffset(
      audio,
      protocolBpmList(view.points),
      view.heardMs + view.previewAudioDelayMs,
    );
  } catch (err) {
    s.ui.toast(ref(m.analysisFailed, { error: errText(err) }), { kind: 'error' });
    return null;
  }
  if (r === null || r.confidence <= 0) {
    s.ui.toast(ref(m.offsetNotFound), { kind: 'warning' });
    return null;
  }
  const heard = r.offsetMs - view.previewAudioDelayMs;
  if (!setHeardOffset(s, heard)) return null;
  const pct = Math.round(r.confidence * 100);
  s.ui.toast(
    ref(r.confidence >= LOW_CONFIDENCE ? m.offsetDetected : m.offsetLowConfidence, { sec: heard / 1000, pct }),
    {
      kind: r.confidence >= LOW_CONFIDENCE ? 'success' : 'warning',
      undo: true,
    },
  );
  return { offsetMs: heard, confidence: r.confidence };
}

/** Beat 0 moves to a decoded music time (s). */
export function beatZeroAt(s: Services, musicSec: number): boolean {
  const store = s.store;
  if (store === null || !Number.isFinite(musicSec)) return false;
  const view = viewOf(store);
  const heard = Math.round(musicSec * 1000 - view.previewAudioDelayMs);
  if (!setHeardOffset(s, heard)) return false;
  s.ui.toast(ref(m.overviewSetBeatZero, { sec: heard / 1000 }), { kind: 'info', undo: true });
  return true;
}

/** One "Too early / Just right / Too late" answer (shrinking steps; Just right confirms). */
export function answerByEar(s: Services, answer: ByEarAnswer): void {
  const session = timingSessionFor(s);
  const { deltaMs, next } = byEarDelta(answer, session.get().byEarIndex);
  session.set({ byEarIndex: next });
  if (answer === 'justRight') {
    if (setConfirmed(s, true)) s.ui.toast(ref(m.byEarConfirmed), { kind: 'success' });
    return;
  }
  nudgeOffset(s, deltaMs);
}

/** Starts or stops "Play with clicks" (metronome forced on while it plays). */
export async function togglePlayWithClicks(s: Services): Promise<void> {
  const session = timingSessionFor(s);
  const transport = s.audio.transport;
  if (session.get().clicks && transport.state.playing) {
    transport.stop();
    stopClicks(s);
    return;
  }
  const editor = s.editor.getState();
  session.set({ clicks: true, clicksRestoreMetronome: editor.metronome });
  if (!editor.metronome) editor.setMetronome(true);
  try {
    await s.audio.ensureContext();
    await transport.play({ mode: 'flow' });
  } catch (err) {
    stopClicks(s);
    s.ui.toast(ref(m.analysisFailed, { error: errText(err) }), { kind: 'error' });
  }
}

/** Ends "Play with clicks": the metronome goes back to what it was. */
export function stopClicks(s: Pick<AppServices, 'editor'>): void {
  const session = timingSessionFor(s);
  const st = session.get();
  if (!st.clicks) return;
  session.set({ clicks: false, clicksRestoreMetronome: null });
  if (st.clicksRestoreMetronome === false) s.editor.getState().setMetronome(false);
}

export function addTempoAtPlayhead(s: Services): boolean {
  if (s.store === null) return false;
  return addTempoAt(s, playheadBeat(s));
}

export function addMeterAtPlayhead(s: Services): boolean {
  if (s.store === null) return false;
  return addMeterAt(s, playheadBeat(s));
}

/** Opens the latency calibration dialog and reports the saved value. */
export async function openCalibration(s: Pick<AppServices, 'ui'>): Promise<void> {
  const result = await s.ui.openDialog(DIALOG.latencyCalibration, null);
  if (result !== null) {
    s.ui.toast(ref(m.calSaved, { ms: Math.round(result.calibrationMs) }), { kind: 'success' });
  }
}
